import { Component, OnInit, OnDestroy, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { MessageService } from 'primeng/api';
import { UbicacionDigitalService } from '../../../services/ubicacion-digital/ubicacion-digital.service';
import { UbicacionDigital } from '../../../domain/ubicacion-digital.domain';

@Component({
  selector: 'app-ubicacion-digital-edit',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    TextareaModule,
    ToggleSwitchModule,
    ButtonModule,
    SelectModule
  ],
  templateUrl: './ubicacion-digital-edit.component.html',
  styleUrls: ['./ubicacion-digital-edit.component.scss']
})
export class UbicacionDigitalEditComponent implements OnInit, OnDestroy {
  ubicacionForm!: FormGroup;
  loading = signal<boolean>(false);
  isEditMode = signal<boolean>(false);
  ubicacionId: string | null = null;
  ubicacion = signal<UbicacionDigital | null>(null);

  canalOptions = [
    { label: 'Web', value: 'WEB' },
    { label: 'Redes', value: 'REDES' }
  ];

  private destroy$ = new Subject<void>();
  private ubicacionDigitalService = inject(UbicacionDigitalService);

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.setupRouteParamListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupRouteParamListener(): void {
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const newId = params.get('id');
        if (newId !== this.ubicacionId) {
          this.loadUbicacion();
        }
      });
  }

  initForm(): void {
    this.ubicacionForm = this.fb.group({
      codigo: ['', Validators.required],
      nombre: ['', Validators.required],
      canal: [null],
      descripcion: [''],
      active: [true]
    });
  }

  loadUbicacion(): void {
    const newUbicacionId = this.route.snapshot.paramMap.get('id');

    if (newUbicacionId !== this.ubicacionId) {
      this.ubicacionForm.reset({ active: true });
      this.ubicacion.set(null);
    }

    this.ubicacionId = newUbicacionId;

    if (this.ubicacionId && this.ubicacionId !== 'nuevo') {
      this.isEditMode.set(true);
      this.loading.set(true);

      this.ubicacionDigitalService.getById(this.ubicacionId!)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (ubicacion: UbicacionDigital) => {
            this.loading.set(false);
            this.ubicacion.set(ubicacion);
            this.ubicacionForm.patchValue({
              codigo: ubicacion.codigo || '',
              nombre: ubicacion.nombre || '',
              canal: ubicacion.canal || null,
              descripcion: ubicacion.descripcion || '',
              active: ubicacion.active ?? true
            });
          },
          error: (error) => {
            this.loading.set(false);
            console.error('Error al cargar ubicación digital:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Error al cargar los datos de la ubicación digital',
              life: 3000
            });
          }
        });
    } else {
      this.isEditMode.set(false);
    }
  }

  onSubmit(): void {
    if (this.ubicacionForm.invalid) {
      this.markFormGroupTouched(this.ubicacionForm);
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulario Inválido',
        detail: 'Por favor complete todos los campos requeridos',
        life: 3000
      });
      return;
    }

    const formValue = this.ubicacionForm.value;

    const ubicacion: UbicacionDigital = {
      codigo: formValue.codigo,
      nombre: formValue.nombre,
      canal: formValue.canal,
      descripcion: formValue.descripcion,
      active: formValue.active ?? true
    };

    if (this.isEditMode()) {
      ubicacion.id = Number(this.ubicacionId);
    }

    this.loading.set(true);

    const operation = this.isEditMode()
      ? this.ubicacionDigitalService.update(this.ubicacionId!, ubicacion)
      : this.ubicacionDigitalService.create(ubicacion);

    operation
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: this.isEditMode() ? 'Ubicación digital actualizada correctamente' : 'Ubicación digital creada correctamente',
            life: 3000
          });
          setTimeout(() => {
            this.router.navigate(['/ubicaciones-digitales']);
          }, 1500);
        },
        error: (error) => {
          this.loading.set(false);
          console.error('Error al guardar ubicación digital:', error);

          let errorMessage = 'Error al guardar la ubicación digital';
          if (error.error?.violations) {
            const violations = error.error.violations;
            errorMessage = violations.map((v: any) => v.message).join(', ');
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          }

          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage,
            life: 5000
          });
        }
      });
  }

  onCancel(): void {
    this.router.navigate(['/ubicaciones-digitales']);
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  get pageTitle(): string {
    return this.isEditMode() ? 'Editar Ubicación Digital' : 'Nueva Ubicación Digital';
  }
}
